import { useQuery } from "@apollo/client";
import styled from "@emotion/styled";
import { GetServerSideProps } from "next";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useRouter } from "next/dist/client/router";
import React from "react";
import Activities from "../../components/activities/Activities";
import BackButton from "../../components/BackButton";
import Box from "../../components/Box";
import CustomLink from "../../components/CustomLink";
import Flex from "../../components/Flex";
import ShopLayout from "../../components/layouts/ShopLayout";
import Title from "../../components/Title";
import { default as MenuSVG } from "../../assets/menu.svg";
import {
  activitiesPageQuery,
  activitiesQuery,
  footerQuery,
  headerQuery,
} from "../../graphql/queries";
import {
  ActivitiesPageQuery,
  ActivitiesQuery,
  ActivitiesQueryVariables,
} from "../../graphql/types";
import { initializeApollo } from "../../libs/apollo-client";
import { Locale, localePath } from "../../routes";
import { mq } from "../../libs/emotion-theme";
import { css } from "@emotion/react";
import Markdown from "markdown-to-jsx";

const ActivitiesPage = () => {
  const { t } = useTranslation("activities");
  const {
    locale,
    query: { filter },
  } = useRouter();

  const { data: pageData } = useQuery<ActivitiesPageQuery>(activitiesPageQuery);

  const allFilters = pageData?.activityFilters?.map((filter) => filter?.id);

  const { data } = useQuery<ActivitiesQuery, ActivitiesQueryVariables>(
    activitiesQuery,
    {
      variables: {
        filters: filter ? [filter as string] : (allFilters as string[]),
      },
      skip: allFilters ? allFilters.length < 1 : true,
    }
  );

  return (
    <ShopLayout title={pageData?.activitiesPage?.seo?.metaTitle} metaDescription={pageData?.activitiesPage?.seo?.metaDescription}>
      <Box px={3}>
        <BackButton href="/" />
        <Flex alignItems="center" flexDirection="column" mb={{ _: 3, xl: 5 }}>
          <Title level={1} css={{ textAlign: "center" }}>
            {pageData?.activitiesPage?.title}
          </Title>
          <Description width={{ _: 1, md: 1 / 2 }} mb={4}>
            <Markdown>{pageData?.activitiesPage?.description || ""}</Markdown>
          </Description>
          <Flex flexWrap="wrap" justifyContent="center">
            <Filter
              href={localePath[locale as Locale].activities}
              ariaLabel="all"
              shallow
              isActive={filter == null}
            >
              <MenuSVG css={{ width: 21 }} />
              {t("showAll")}
            </Filter>
            {pageData?.activityFilters?.map((activityFilter) => (
              <Filter
                href={{ pathname: localePath[locale as Locale].activities, query: { filter: activityFilter?.id } }}
                shallow
                ariaLabel={activityFilter?.title}
                isActive={(filter as string) === activityFilter?.id}
                key={activityFilter?.id}
              >
                <img
                  src={activityFilter?.icon?.url || ""}
                  alt={activityFilter?.icon?.alternativeText || ""}
                />
                {activityFilter?.title}
              </Filter>
            ))}
          </Flex>
        </Flex>
        <Activities activities={data?.activities} />
      </Box>
    </ShopLayout>
  );
};

export default ActivitiesPage;

const Description = styled(Box)`
  text-align: center;
`;

const Filter = styled(CustomLink) <{ isActive?: boolean }>`
  flex-direction: column;
  justify-content: center;
  align-items: center;
  display: flex;
  text-transform: uppercase;
  font-weight: 700;
  margin: 0.75rem;

  ::after {
    content: "";
    position: absolute;
    right: 50%;
    bottom: -2px;
    left: 50%;
    height: 2px;
    background-color: ${({ theme }) => theme.colors.text};
    transition: right 0.2s ease-in-out, left 0.2s ease-in-out;
  }

  ${({ isActive }) =>
    isActive &&
    css`
      ::after {
        right: 0;
        left: 0;
      }
    `}

  ${mq[1]} {
    margin: 21px;
  }

  :hover {
    ::after {
      right: 0;
      left: 0;
    }
  }

  img,
  svg {
    margin-bottom: 8px !important;
    height: 22px;
    display: none;

    ${mq[1]} {
      display: block;
    }
  }
`;

export const getServerSideProps: GetServerSideProps = async ({
  locale,
  query,
}) => {
  const { filter } = query;
  const apolloClient = initializeApollo(null, locale);

  const { data } = await apolloClient.query({
    query: activitiesPageQuery,
  });

  const allFilters = (data as ActivitiesPageQuery)?.activityFilters?.map(
    (filter) => filter?.id
  );

  await apolloClient.query({
    query: activitiesQuery,
    variables: {
      filters: filter ? [filter as string] : (allFilters as string[]),
    },
  });
  await apolloClient.query({
    query: headerQuery
  });
  await apolloClient.query({
    query: footerQuery,
  });

  return {
    props: {
      initialApolloState: apolloClient.cache.extract(),
      ...(await serverSideTranslations(locale as string, [
        "activities",
        "common",
      ])),
    },
  };
};
